import Bugsnag from '@bugsnag/js'
import { doShowSnackbar } from 'actions/snackbar'
import graphql from 'api/graphql'
import * as types from 'constants/action_types'
import { oauthTokenSelector } from 'selectors/app'
import { selectCurrentTicketId } from 'selectors/tickets'
import { selectCurrentUser } from 'ducks/currentUser/selectors'
import config from 'config'
import debug, { logError } from 'util/debug'
import { toPlainObject } from 'util/file'
import { rfc3986EncodeURIComponent } from 'util/strings'

function buildBugsnagError(title, error) {
  return new Error(title, {
    severity: 'error',
    metaData: {
      meta: { error },
    },
  })
}

function notify(msg, err) {
  debug(msg, err)
  Bugsnag.notify(buildBugsnagError(msg, err))
}

export function doUploadFile(state, dispatch, file, s3, forNote, draftId) {
  const ticketId = selectCurrentTicketId(state)
  const s3Url = `https://${s3.bucket}.${config.s3_url}/`
  const { expiringUrl, key } = s3
  const fullUrl = s3Url + key
  const plainFileObject = toPlainObject(file)

  dispatch({
    type: types.ATTACHMENT_UPLOAD_STARTED,
    data: {
      ticketId,
      file: plainFileObject,
      key,
      forNote,
      expiringUrl,
      draftId,
    },
  })

  return new Promise((resolve, reject) => {
    const formData = new FormData()
    const xhr = new XMLHttpRequest()

    formData.append('key', key)
    formData.append('acl', s3.acl)
    formData.append('Content-Disposition', 'attachment')
    formData.append('Cache-Control', 'max-age=31536000')
    formData.append('AWSAccessKeyId', s3.accessKey)
    formData.append('policy', s3.policy)
    formData.append('signature', s3.signature)
    formData.append('file', file)

    xhr.upload.onprogress = e => {
      const { total, loaded } = e
      const percentage = Math.round(loaded / total * 100)

      dispatch({
        type: types.ATTACHMENT_UPLOAD_PROGRESSED,
        data: {
          draftId,
          file: plainFileObject,
          percentage,
          ticketId,
          forNote,
          expiringUrl,
        },
      })
    }
    xhr.onload = e => {
      /* eslint-disable eqeqeq */
      const { status } = e.target
      if (status === 200 || status === 204) {
        /* eslint-enable eqeqeq */
        const payload = {
          file: plainFileObject,
          url: fullUrl,
          ticketId,
          key,
          forNote,
          expiringUrl,
          draftId,
        }
        resolve(payload)
        dispatch({
          type: types.ATTACHMENT_UPLOAD_FINISHED,
          data: payload,
        })
      } else {
        notify('Attachment upload: API error', e)
      }
    }
    xhr.onerror = err => {
      notify('Attachment upload: network error', err)

      reject({ status: xhr.status, error: err })
      dispatch({
        type: types.ATTACHMENT_UPLOAD_FAILED,
        data: {
          draftId,
          ticketId,
          file: plainFileObject,
          key,
          forNote,
          expiringUrl,
          error: err,
          status: xhr.status,
        },
      })
    }

    xhr.open('POST', s3Url)
    xhr.send(formData)
  })
}

export function getSignature(state, key) {
  return new Promise(resolve => {
    const token = oauthTokenSelector(state)
    const query = `
      query UploaderPayload($key: String) {
        uploaderPayload(key: $key) {
          accessKey
          key
          policy
          signature
          bucket
          acl
          expiration
          expiringUrl
        }
      }
    `
    const variables = { key }
    graphql(token, query, variables).then(res => {
      resolve(res.json.data.uploaderPayload)
    })
  })
}

export function doUploadFiles(fileList, forNote, draftId) {
  return (dispatch, getState) => {
    const state = getState()

    // We need a presigned URL for each file.
    const promises = []

    for (let i = 0; i < fileList.length; i += 1) {
      const file = toPlainObject(fileList[i])
      const encodedFilename = rfc3986EncodeURIComponent(file.name)
      const key = `uploads/${new Date().getTime()}-${encodedFilename}`
      promises.push(
        getSignature(state, key).then(s3 => {
          return doUploadFile(
            state,
            dispatch,
            fileList[i],
            s3,
            forNote,
            draftId
          )
        })
      )
    }

    return Promise.all(promises).catch(err => {
      logError(err)
      dispatch(
        doShowSnackbar(
          'Oops. There was a problem while uploading your attachments'
        )
      )
    })
  }
}

export function doDeleteFile(file, forNote, draftId) {
  return (dispatch, getState) => {
    const state = getState()
    const ticketId = selectCurrentTicketId(state)

    dispatch({
      type: types.DELETE_ATTACHMENT,
      data: {
        ticketId,
        file,
        forNote,
        draftId,
      },
    })
  }
}

export function doUploadHistory() {
  return (dispatch, getState) => {
    const state = getState()
    const date = new Date().toJSON()
    const payload = {
      actions: window.actionHistory.entries,
      state,
    }
    const contents = JSON.stringify(payload)
    const currentUser = selectCurrentUser(state)
    const filename = `errordumps/${currentUser.id}-${date}.json`
    const file = new File([contents], filename, { type: 'application/json' })
    // eslint-disable-next-line no-unused-vars
    const handler = _action => {}
    return getSignature(state, filename).then(s3 => {
      return doUploadFile(state, handler, file, s3, false)
    })
  }
}

window.doUploadHistory = doUploadHistory
